import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Avatar,
  Typography,
  Button,
} from "@material-tailwind/react";
import {
  UserCircleIcon,
  ChevronDownIcon,
  PowerIcon,
} from "@heroicons/react/24/solid";
import { useReduxStore } from "../../hooks/useReduxStore.js";
import { LogoutAction } from "../../store/Actions/action.js";
import { updateItem } from "../../store/Reducers/itemReducer.js";
import { getFirstName, scrollToTop } from "../../helper/helper.js";

// Assets imports
import bgHomeS1 from "../../assets/landingPage/bg-home-s1.png";
import bgHomeS2 from "../../assets/landingPage/bg-home-s2.png";
import { newLogo } from "../../assets/Images";
import audiobookIcon from "../../assets/landingPage/audiobook.png";
import ebookIcon from "../../assets/landingPage/ebook.png";
import moviesIcon from "../../assets/landingPage/movies.png";
import songsIcon from "../../assets/landingPage/songs.png";
import exclusiveIcon from "../../assets/landingPage/exclusive.png";

function ProfileMenu() {
  const { dispatch, getState } = useReduxStore();
  const { userData } = getState("AUTH");
  const { avatar } = userData || {};
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const navigate = useNavigate();
  const closeMenu = () => setIsMenuOpen(false);

  const onProfileMenuClick = (route, isLastItem) => {
    if (isLastItem) {
      dispatch(LogoutAction());
    }
    navigate(route);
    if (isLastItem) {
      scrollToTop();
    }
    closeMenu();
  };

  return (
    <Menu open={isMenuOpen} handler={setIsMenuOpen} placement="bottom-end">
      <MenuHandler>
        <Button
          variant="text"
          color="blue-gray"
          className="flex items-center gap-1.5 rounded-full py-0.5 pr-2 pl-0.5 outline-none focus:outline-none"
        >
          <Avatar
            variant="circular"
            size="sm"
            alt={userData?.name || "User"}
            className="border border-secondary p-0.5"
            src={
              avatar ||
              "https://i.pinimg.com/originals/5f/40/6a/5f406ab25e8942cbe0da6485afd26b71.jpg"
            }
          />
          <span className="px-1 text-white text-sm font-medium font-outfit hidden sm:inline-block">
            {getFirstName(userData?.name)}
          </span>
          <ChevronDownIcon
            strokeWidth={2.5}
            className={`h-3 w-3 text-white transition-transform ${isMenuOpen ? "rotate-180" : ""
              }`}
          />
        </Button>
      </MenuHandler>
      <MenuList className="p-1 border border-neutral-800 bg-[#111] text-white z-[9999999]">
        {profileMenuItems.map(({ label, icon, route }, key) => {
          const isLastItem = key === profileMenuItems.length - 1;
          return (
            <MenuItem
              key={label}
              onClick={() => onProfileMenuClick(route, isLastItem)}
              className={`flex items-center gap-2 rounded hover:bg-neutral-800 focus:bg-neutral-800 active:bg-neutral-800 py-2 px-3 ${isLastItem ? "text-red-500 hover:text-red-400" : "text-white"
                }`}
            >
              {React.createElement(icon, {
                className: `h-4 w-4 ${isLastItem ? "text-red-500" : "text-secondary"}`,
                strokeWidth: 2,
              })}
              <Typography
                as="span"
                variant="small"
                className="font-normal font-outfit"
              >
                {label}
              </Typography>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}

const profileMenuItems = [
  {
    label: "My Account",
    icon: UserCircleIcon,
    route: "/Profile",
  },
  {
    label: "Sign Out",
    icon: PowerIcon,
    route: "/",
  },
];

export default function HeroSectionNew({ onCategorySelect }) {
  const navigate = useNavigate();
  const { getState, dispatch } = useReduxStore();
  const { token } = getState("AUTH");

  const categoriesList = [
    {
      name: "AudioBooks",
      icon: audiobookIcon,
    },
    {
      name: "EBooks",
      icon: ebookIcon,
    },
    {
      name: "Movies",
      icon: moviesIcon,
    },
    {
      name: "Songs",
      icon: songsIcon,
    },
    {
      name: "Exclusive Content",
      icon: exclusiveIcon,
    },
  ];

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openSearchModal = () => dispatch(updateItem({ searchModal: true }));

  return (
    <div className="w-full flex flex-col select-none">
      {/* ================= FIRST SECTION (Background: bg-home-s1.png) ================= */}
      <div
        className="w-full bg-cover bg-center pt-4 pb-20 px-4 md:px-8 lg:px-12 relative flex flex-col items-center"
        style={{ backgroundImage: `url(${bgHomeS1})` }}
      >
        <div className="absolute z-10 inset-0 bg-black/70 pointer-events-none"></div>
        {/* Navbar inside the background container */}
        <div className="w-full z-50 max-w-7xl flex items-center justify-between py-4 md:py-2 mb-16">
          <Link to="/" className="flex flex-col items-center">
            <img
              className="object-contain h-10 sm:h-12 md:h-20 w-auto"
              src={newLogo}
              alt="BAB Logo"
            />

          </Link>

          <div className="flex items-center gap-4">
            {/* Search Icon Button */}
            <button
              onClick={openSearchModal}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg border border-neutral-700/60 flex items-center justify-center text-white hover:border-[#FF8C21] hover:text-[#FF8C21] transition-all bg-black/20 outline-none focus:outline-none"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>

            {/* Login Button or Profile Dropdown */}
            {!token ? (
              <button
                onClick={() => navigate("/Login", { state: { goBack: true } })}
                className="px-5 py-1.5 sm:px-6 sm:py-2 bg-[#FF8C21] hover:bg-white text-black font-semibold rounded-lg text-xs sm:text-sm transition-all duration-300 font-outfit active:scale-95"
              >
                Login
              </button>
            ) : (
              <ProfileMenu />
            )}
          </div>
        </div>

        {/* Center Title Block */}
        <div className="flex z-50 flex-col items-center text-center max-w-4xl px-4">
          <h1 className="text-6xl md:text-7xl lg:text-9xl tracking-[8px]  font-semibold font-oswald text-secondary mb-6 leading-none uppercase">
            BAB MEDIA
          </h1>
          <p className="text-xl md:text-2xl lg:text-4xl font-outfit  text-white tracking-widest uppercase">
            — Boss Amongst Bosses —
          </p>
        </div>
      </div>

      {/* ================= SECOND SECTION (Background: bg-home-s2.png) ================= */}
      <div
        className="w-full bg-cover bg-center py-16 pt-2 px-4 md:px-8 lg:px-12 relative flex flex-col items-center"
        style={{ backgroundImage: `url(${bgHomeS2})` }}
      >
        <div className="absolute z-10 inset-0 bg-black/70 pointer-events-none"></div>
        {/* Navigation Menu */}
        <div className="z-50 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 max-w-6xl py-2 px-6 mb-12 select-none">
          <Link
            to="/"
            className="text-white border-b-2 border-[#FF8C21] font-outfit text-sm md:text-base lg:text-lg font-medium px-1 pb-1 transition-colors"
          >
            Home
          </Link>
          <Link
            to="/news-new"
            className="text-gray-400 hover:text-white font-outfit text-sm md:text-base lg:text-lg font-medium px-1 pb-1 transition-colors"
          >
            BAB Media News & Exclusives
          </Link>
          <Link
            to="/digital-library"
            className="text-gray-400 hover:text-white font-outfit text-sm md:text-base lg:text-lg font-medium px-1 pb-1 transition-colors cursor-pointer outline-none focus:outline-none"
          >
            Digital Library
          </Link>
          <Link
            to="/soulflow-records"
            className="text-gray-400 hover:text-white font-outfit text-sm md:text-base lg:text-lg font-medium px-1 pb-1 transition-colors cursor-pointer outline-none focus:outline-none"
          >
            B.A.B SoulFlow Records AJB
          </Link>
          <Link
            to="/mystio-new"
            className="text-gray-400 hover:text-white font-outfit text-sm md:text-base lg:text-lg font-medium px-1 pb-1 transition-colors cursor-pointer outline-none focus:outline-none"
          >
            Mystio Global Logistics
          </Link>
        </div>

        {/* 5 Category Cards Grid */}
        <div className="z-50 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 w-full max-w-6xl">
          {categoriesList.map((cat, idx) => (
            <div
              key={idx}
              onClick={() => onCategorySelect(cat.name)}
              className="flex flex-col rounded-xl border-[3.5px] border-secondary/60 bg-black/45 hover:border-secondary transition-all duration-300 transform hover:-translate-y-1.5 cursor-pointer shadow-lg overflow-hidden group"
            >
              {/* Top part: Icon */}
              <div className="py-6 px-4 flex items-center justify-center flex-1 bg-black/10">
                <img
                  src={cat.icon}
                  alt={cat.name}
                  className="w-20 h-20 sm:w-32 sm:h-32 object-contain transform group-hover:scale-105 duration-300"
                />
              </div>

              {/* Divider line */}
              <div className="w-full border-2 border-t border-secondary/60 group-hover:border-secondary transition-colors"></div>

              {/* Bottom part: Name */}
              <div className="py-3 px-2 bg-black/40 text-center">
                <span className="text-secondary text-xs sm:text-sm md:text-base lg:text-lg font-semibold font-outfit tracking-wider block">
                  {cat.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
